package com.swetdot.swetgram.data.profile

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.domain.MessageAudience
import com.swetdot.swetgram.domain.PresenceAudience
import com.swetdot.swetgram.domain.PresenceState
import com.swetdot.swetgram.domain.PrivacySettings
import com.swetdot.swetgram.protocol.dto.UpdatePresenceRequest
import io.ktor.client.request.setBody
import com.swetdot.swetgram.protocol.dto.MessageAudience as MessageAudienceDto
import com.swetdot.swetgram.protocol.dto.PresenceAudience as PresenceAudienceDto
import com.swetdot.swetgram.protocol.dto.PresenceState as PresenceStateDto
import com.swetdot.swetgram.protocol.dto.PrivacySettings as PrivacySettingsDto

/**
 * Privacy settings and presence.
 *
 * Nothing here is cached. A privacy control that renders from a stale copy is a control that can
 * show "last seen: nobody" while the server is still publishing it, which is the one lie this
 * screen must not tell -- so every read is a read.
 */
class PrivacyRepository(
    private val api: ApiClient,
) {
    /** `getPrivacySettings`. */
    suspend fun settings(): PrivacySettings = api.get<PrivacySettingsDto>("users/me/privacy").toDomain()

    /**
     * `updatePrivacySettings`.
     *
     * The contract's update schema is the whole `PrivacySettings` object -- all four properties
     * required -- so this is a REPLACEMENT, not a patch, and there is deliberately no
     * `updateShowOnline`-style helper. Such a helper would have to read the current settings
     * first, and the value it read could already be a second device's change, which it would then
     * write back over.
     *
     * Callers therefore edit the object they are displaying: `update(current.copy(...))`.
     *
     * Returns the SERVER's copy rather than the argument, so the screen renders what was actually
     * stored instead of what was requested.
     */
    suspend fun update(settings: PrivacySettings): PrivacySettings =
        api.patch<PrivacySettingsDto>("users/me/privacy") { setBody(settings.toDto()) }.toDomain()

    /**
     * `updatePresence`. 204.
     *
     * DECISIONS §4: presence is REPORTED -- the app declares `online` when it enters the
     * foreground and `offline` when it leaves, and the server keeps a 60-second grace period
     * after an unexpected disconnect. It is never inferred from request traffic, so a background
     * sync must not call this.
     *
     * Carries no idempotency key, so [ApiClient] does not retry it. That is right for a
     * declaration of a state that is true *now*: by the time two backoffs had elapsed the app
     * could be in the background, and the correct recovery is the next transition declaring the
     * state that holds then, not a replay of a stale one. The contract also declares no
     * `Idempotency-Key` on this operation, and sending an undeclared header to buy a retry would
     * be inventing wire behaviour.
     */
    suspend fun updatePresence(state: PresenceState) {
        api.post<Unit>("presence") { setBody(UpdatePresenceRequest(state = state.toDto())) }
    }

    /** Call when the app enters the foreground. */
    suspend fun goOnline() = updatePresence(PresenceState.ONLINE)

    /** Call when the app leaves the foreground or signs out cleanly. */
    suspend fun goOffline() = updatePresence(PresenceState.OFFLINE)
}

// ------------------------------------------------------------------------------- mapping

/*
 * Both directions fail CLOSED on a value this build does not recognise.
 *
 * `PrivacySettings` has no defaulted properties, so `coerceInputValues` has nothing to coerce to
 * and an unknown enum member is a decode failure long before it reaches here -- these branches
 * are unreachable today. They are still written the strict way, because the alternative reading
 * ("everyone") would publish presence for a user whose stored setting this client simply could
 * not parse, and a privacy control that guesses must guess towards silence.
 */

internal fun PrivacySettingsDto.toDomain(): PrivacySettings = PrivacySettings(
    showOnline = showOnline.toDomain(),
    showLastSeen = showLastSeen.toDomain(),
    showDateOfBirth = showDateOfBirth.toDomain(),
    showLocation = showLocation.toDomain(),
    allowMessagesFrom = allowMessagesFrom.toDomain(),
    allowProfileLink = allowProfileLink,
)

internal fun PrivacySettings.toDto(): PrivacySettingsDto = PrivacySettingsDto(
    showOnline = showOnline.toDto(),
    showLastSeen = showLastSeen.toDto(),
    showDateOfBirth = showDateOfBirth.toDto(),
    showLocation = showLocation.toDto(),
    allowMessagesFrom = allowMessagesFrom.toDto(),
    allowProfileLink = allowProfileLink,
)

private fun PresenceAudienceDto.toDomain(): PresenceAudience = when (this) {
    PresenceAudienceDto.EVERYONE -> PresenceAudience.EVERYONE
    PresenceAudienceDto.SHARED_GROUPS -> PresenceAudience.SHARED_GROUPS
    PresenceAudienceDto.NOBODY -> PresenceAudience.NOBODY
    PresenceAudienceDto.UNKNOWN -> PresenceAudience.NOBODY
}

private fun PresenceAudience.toDto(): PresenceAudienceDto = when (this) {
    PresenceAudience.EVERYONE -> PresenceAudienceDto.EVERYONE
    PresenceAudience.SHARED_GROUPS -> PresenceAudienceDto.SHARED_GROUPS
    PresenceAudience.NOBODY -> PresenceAudienceDto.NOBODY
}

/** Note the strictest member is `existing_chats`; the contract has no `nobody` here. */
private fun MessageAudienceDto.toDomain(): MessageAudience = when (this) {
    MessageAudienceDto.EVERYONE -> MessageAudience.EVERYONE
    MessageAudienceDto.SHARED_GROUPS -> MessageAudience.SHARED_GROUPS
    MessageAudienceDto.EXISTING_CHATS -> MessageAudience.EXISTING_CHATS
    MessageAudienceDto.UNKNOWN -> MessageAudience.EXISTING_CHATS
}

private fun MessageAudience.toDto(): MessageAudienceDto = when (this) {
    MessageAudience.EVERYONE -> MessageAudienceDto.EVERYONE
    MessageAudience.SHARED_GROUPS -> MessageAudienceDto.SHARED_GROUPS
    MessageAudience.EXISTING_CHATS -> MessageAudienceDto.EXISTING_CHATS
}

private fun PresenceState.toDto(): PresenceStateDto = when (this) {
    PresenceState.ONLINE -> PresenceStateDto.ONLINE
    PresenceState.OFFLINE -> PresenceStateDto.OFFLINE
}
